import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  Button,
  TextField,
  Alert,
  IconButton
} from '@mui/material';
import { Warehouse, Home } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Validation schema
const productSchema = yup.object().shape({
  name: yup.string().required('Product name is required').min(2, 'Minimum 2 characters'),
  quantity: yup.number().required('Quantity is required').min(1, 'Minimum quantity is 1'),
  price: yup.number().required('Price is required').min(0, 'Price must be positive'),
  description: yup.string().max(200, 'Maximum 200 characters').default('')
});

type ProductFormData = {
  name: string;
  quantity: number;
  price: number;
  description: string;
};

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function ReactHookFormPage() {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<ProductFormData>({
    resolver: yupResolver(productSchema),
    defaultValues: {
      name: '',
      quantity: 1,
      price: 0,
      description: ''
    }
  });

  const onSubmit: SubmitHandler<ProductFormData> = async (data) => {
    console.log('Form Data:', data);
    alert(`Product registered!\nName: ${data.name}\nQuantity: ${data.quantity}\nPrice: $${data.price}`);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const watchedValues = watch();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <IconButton 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Warehouse sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            React Hook Form Practice
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            React Hook Form Testing
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Step 3: Learning React Hook Form with validation
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          
          {/* Form Section */}
          <Box sx={{ flex: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Product Registration Form
              </Typography>
              
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ '& > :not(style)': { mb: 2 } }}>
                  
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Product Name"
                        variant="outlined"
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />

                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Quantity"
                        type="number"
                        variant="outlined"
                        fullWidth
                        error={!!errors.quantity}
                        helperText={errors.quantity?.message}
                      />
                    )}
                  />

                  <Controller
                    name="price"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Price ($)"
                        type="number"
                        variant="outlined"
                        fullWidth
                        error={!!errors.price}
                        helperText={errors.price?.message}
                      />
                    )}
                  />

                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    )}
                  />

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Registering...' : 'Register Product'}
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => reset()}
                    >
                      Reset Form
                    </Button>
                  </Box>

                </Box>
              </form>
            </Paper>
          </Box>

          {/* Real-time Preview */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Real-time Preview
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2"><strong>Name:</strong> {watchedValues.name || 'Not entered'}</Typography>
                <Typography variant="body2"><strong>Quantity:</strong> {watchedValues.quantity}</Typography>
                <Typography variant="body2"><strong>Price:</strong> ${watchedValues.price}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {watchedValues.description || 'Not entered'}</Typography>
              </Box>

              {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Please fix the form errors above
                </Alert>
              )}
            </Paper>
          </Box>

        </Box>

        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Form Features
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Real-time validation with Yup schema<br/>
            • Live preview of form values<br/>
            • Error handling and user feedback<br/>
            • Form reset functionality<br/>
            • Loading states during submission
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              component={Link}
              to="/mui-advanced"
              variant="contained"
              disabled
            >
              Next: Advanced Forms (Coming Soon)
            </Button>
            <Button 
              component={Link}
              to="/mui-basics"
              variant="outlined"
            >
              Previous: MUI Basics
            </Button>
            <Button 
              component={Link}
              to="/"
              variant="outlined"
            >
              Back to Home
            </Button>
          </Box>
        </Paper>

      </Container>
    </ThemeProvider>
  );
}

export default ReactHookFormPage;
